import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import PageHeader from "../../../app/layout/PageHeader";
import { api, ApiError } from "../../../api/apiClient";
import { useAuth } from "../../../context/AuthContext";
import { TENANT_ROLE_CODES } from "../../../constants/constants";
import { DocumentActions } from "../../../shared/document-export";
import UserPartyLink from "../../../components/UserPartyLink";
import { notify } from "../../components-ui/toast";
import "./SupplierInvoicesPage.css";

type InvoiceLine = {
  supplierInvoiceLineId: number;
  serviceInstanceRotaId: number;
  serviceInstanceId: number;
  serviceId: number;
  serviceName: string;
  sessionStartDateTime: string;
  sessionEndDateTime: string;
  workStartDateTime?: string;
  workEndDateTime?: string;
  hoursClaimed: number;
  rateType: string;
  rate: number;
  vatRate: number;
  lineSubtotal: number;
  lineVatAmount: number;
  lineTotal: number;
  adjustmentReason?: string;
};

type Invoice = {
  supplierInvoiceId: number;
  invoiceNumber?: string;
  status: string;
  invoiceDate?: string;
  dueDate?: string;
  supplierName: string;
  supplierEmailAddress: string;
  supplierIsSelfEmployed: boolean;
  paymentDetails?: {
    bankAccountName: string;
    bankName?: string;
    sortCode: string;
    accountNumber: string;
    paymentReference?: string;
  };
  tenantName: string;
  tenantInvoiceEmailAddress: string;
  subtotal: number;
  vatAmount: number;
  total: number;
  reviewComment?: string;
  submittedDate?: string;
  lines: InvoiceLine[];
};

type EligibleSession = {
  serviceInstanceRotaId: number;
  serviceInstanceId: number;
  serviceId: number;
  serviceName: string;
  startDateTime: string;
  endDateTime: string;
  scheduledHours: number;
  rateType: string;
  rate: number;
};

type Selection = { selected: boolean; hours: number; reason: string; workStart?: string; workEnd?: string };
type SessionEligibility = {
  supplierName: string;
  partyId: number;
  fromDate: string;
  toDate: string;
  assignmentCount: number;
  excludedSessions: { serviceInstanceRotaId: number; serviceName: string; startDateTime: string; reasons: string[] }[];
};
type Supplier = { supplierProfileId: number; supplierName: string; invoiceEmailAddress: string; userEmailAddress: string; isSelfEmployed: boolean; hasPaymentDetails?: boolean; bankAccountLastFour?: string; partyId?: number; userId?: string; vatRegistered?: boolean; vatRegistrationNumber?: string; vatRate?: number; invoicePrefix?: string; isActive?: boolean };
type SupplierSummary = Supplier & { invoiceCount: number; invoiceTotal: number };
type SupplierRate = { supplierServiceRateId: number; supplierProfileId: number; serviceId: number; serviceName: string; rateType: string; rate: number; effectiveFrom: string; effectiveTo?: string; isActive: boolean };
type UserOption = { id: string; email: string; firstName: string; lastName: string; tenants?: { roles: string[] }[] };
type ServiceOption = { serviceId: number; name: string };

const money = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" });
const date = (value?: string) => value ? new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
const time = (value: string) => new Date(value).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
export const localWorkDateTime = (value: string) => {
  const instant = new Date(value);
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${instant.getFullYear()}-${pad(instant.getMonth() + 1)}-${pad(instant.getDate())}T${pad(instant.getHours())}:${pad(instant.getMinutes())}`;
};
export const workingHours = (start: string, end: string) => {
  const hours = (new Date(end).getTime() - new Date(start).getTime()) / 3600000;
  return Number.isFinite(hours) ? Math.round(hours * 100) / 100 : 0;
};
const currentInvoiceDate = () => {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const part = (type: string) => parts.find((item) => item.type === type)?.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
};

function getError(error: unknown) {
  if (error instanceof ApiError && error.body && typeof error.body === "object" && "message" in error.body) {
    return String((error.body as { message?: string }).message ?? error.message);
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}

function reportInvoiceError(error: unknown) {
  // API failures already use the global notification handler.
  if (!(error instanceof ApiError)) notify.error(getError(error));
}

export default function SupplierInvoicesPage() {
  const { invoiceId } = useParams<{ invoiceId?: string }>();
  const { hasTenantRole, isTenantAdmin, selectedTenant } = useAuth();
  const canCreate = hasTenantRole(TENANT_ROLE_CODES.SELF_EMPLOYED_STAFF);
  const canReview = hasTenantRole(TENANT_ROLE_CODES.INVOICE_REVIEWER) || isTenantAdmin;
  const [tab, setTab] = useState<"suppliers" | "create" | "mine" | "review" | "setup">(
    isTenantAdmin ? "suppliers" : canCreate ? "mine" : "review",
  );
  const [sessions, setSessions] = useState<EligibleSession[]>([]);
  const [eligibility, setEligibility] = useState<SessionEligibility | null>(null);
  const [selection, setSelection] = useState<Record<number, Selection>>({});
  const [mine, setMine] = useState<Invoice[]>([]);
  const [review, setReview] = useState<Invoice[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplierProfileId, setSelectedSupplierProfileId] = useState<number | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [editingInvoiceId, setEditingInvoiceId] = useState<number | null>(null);
  const [invoiceDates, setInvoiceDates] = useState(() => ({ invoiceDate: currentInvoiceDate(), dueDate: "" }));

  const loadInvoices = useCallback(async () => {
    const [myInvoices, reviewInvoices, supplierRows] = await Promise.all([
      canCreate ? api<Invoice[]>("/api/supplier-invoices/mine") : Promise.resolve([]),
      canReview ? api<Invoice[]>("/api/supplier-invoices/review") : Promise.resolve([]),
      isTenantAdmin ? api<Supplier[]>("/api/supplier-invoices/suppliers") : Promise.resolve([]),
    ]);
    setMine(myInvoices);
    setReview(reviewInvoices);
    setSuppliers(supplierRows);
  }, [canCreate, canReview, isTenantAdmin]);

  const loadSessions = useCallback(async () => {
    if (!canCreate) return;
    const [rows, eligibilityResult] = await Promise.all([
      api<EligibleSession[]>("/api/supplier-invoices/eligible-sessions"),
      api<SessionEligibility>("/api/supplier-invoices/session-eligibility"),
    ]);
    setSessions(rows);
    setEligibility(eligibilityResult);
    setSelection(Object.fromEntries(rows.map((row) => [row.serviceInstanceRotaId, {
      selected: false, hours: row.scheduledHours, reason: "",
      workStart: localWorkDateTime(row.startDateTime), workEnd: localWorkDateTime(row.endDateTime),
    }])));
  }, [canCreate]);

  useEffect(() => {
    void loadInvoices().catch((e) => reportInvoiceError(e));
    void loadSessions().catch((e) => reportInvoiceError(e));
  }, [loadInvoices, loadSessions]);

  useEffect(() => {
    if (!invoiceId) return;
    api<Invoice>(`/api/supplier-invoices/${invoiceId}`)
      .then(setSelectedInvoice).catch((e) => reportInvoiceError(e));
  }, [invoiceId]);

  const draftTotal = useMemo(() => sessions.reduce((total, session) => {
    const value = selection[session.serviceInstanceRotaId];
    if (!value?.selected) return total;
    return total + (session.rateType === "Hourly" ? value.hours * session.rate : session.rate);
  }, 0), [sessions, selection]);

  const supplierSummaries = useMemo<SupplierSummary[]>(() => {
    const summaries = new Map<string, SupplierSummary>();
    suppliers.forEach((supplier) => {
      summaries.set(supplier.invoiceEmailAddress.toLowerCase(), {
        ...supplier,
        invoiceCount: 0,
        invoiceTotal: 0,
      });
    });
    review.forEach((invoice) => {
      const key = invoice.supplierEmailAddress.toLowerCase();
      const current = summaries.get(key) ?? {
        supplierProfileId: -invoice.supplierInvoiceId,
        supplierName: invoice.supplierName,
        invoiceEmailAddress: invoice.supplierEmailAddress,
        userEmailAddress: invoice.supplierEmailAddress,
        isSelfEmployed: invoice.supplierIsSelfEmployed,
        isActive: true,
        invoiceCount: 0,
        invoiceTotal: 0,
      };
      current.invoiceCount += 1;
      current.invoiceTotal += invoice.total;
      summaries.set(key, current);
    });
    return [...summaries.values()].sort((left, right) =>
      left.supplierName.localeCompare(right.supplierName),
    );
  }, [review, suppliers]);

  const openCreate = useCallback(() => {
    setEditingInvoiceId(null);
    setInvoiceDates({ invoiceDate: currentInvoiceDate(), dueDate: "" });
    setSelectedInvoice(null);
    setSelectedSupplierProfileId(null);
    void loadSessions().catch((e) => reportInvoiceError(e));
    setTab("create");
  }, [loadSessions]);

  const closeForm = useCallback(() => {
    setEditingInvoiceId(null);
    setSelectedInvoice(null);
    setSelectedSupplierProfileId(null);
    setTab(isTenantAdmin ? "suppliers" : canCreate ? "mine" : "review");
  }, [canCreate, isTenantAdmin]);

  const headerAction = useMemo(() => {
    const editing = tab === "create" || tab === "setup";
    const canAddSupplier = tab === "suppliers" && isTenantAdmin;
    const canAddInvoice = tab === "mine" && canCreate;
    if (!editing && !canAddSupplier && !canAddInvoice) return undefined;
    const addSupplier = () => {
      setSelectedSupplierProfileId(null);
      setSelectedInvoice(null);
      setTab("setup");
    };
    return (
      <button
        type="button"
        className={`icon-button-solid ${
          editing
            ? "icon-button-solid--danger"
            : "icon-button-solid--success"
        }`}
        onClick={editing ? closeForm : canAddSupplier ? addSupplier : openCreate}
        title={editing ? "Cancel" : canAddSupplier ? "Add supplier" : "Create invoice"}
        aria-label={editing ? "Cancel" : canAddSupplier ? "Add supplier" : "Create invoice"}
      >
        {editing ? <CloseIcon fontSize="small" /> : <AddIcon fontSize="small" />}
      </button>
    );
  }, [canCreate, closeForm, isTenantAdmin, openCreate, tab]);

  const saveInvoice = async (submit: boolean) => {
    try {
      const lines = sessions.filter((x) => selection[x.serviceInstanceRotaId]?.selected).map((x) => ({
        serviceInstanceRotaId: x.serviceInstanceRotaId,
        hoursClaimed: Number(selection[x.serviceInstanceRotaId].hours),
        adjustmentReason: selection[x.serviceInstanceRotaId].reason || null,
        workStartDateTime: selection[x.serviceInstanceRotaId].workStart ? new Date(selection[x.serviceInstanceRotaId].workStart!).toISOString() : null,
        workEndDateTime: selection[x.serviceInstanceRotaId].workEnd ? new Date(selection[x.serviceInstanceRotaId].workEnd!).toISOString() : null,
      }));
      const draft = await api<Invoice>("/api/supplier-invoices/drafts", { method: "POST", body: { supplierInvoiceId: editingInvoiceId, invoiceDate: invoiceDates.invoiceDate || null, dueDate: invoiceDates.dueDate || null, lines } });
      const saved = submit
        ? await api<Invoice>(`/api/supplier-invoices/${draft.supplierInvoiceId}/submit`, { method: "POST" })
        : draft;
      setSelectedInvoice(saved);
      notify.success(submit ? `Invoice ${saved.invoiceNumber} was submitted for review.` : "Draft invoice saved.");
      await Promise.all([loadInvoices(), loadSessions()]);
      setEditingInvoiceId(null);
      setTab("mine");
    } catch (e) { reportInvoiceError(e); }
  };

  const reviewInvoice = async (action: "approve" | "query" | "reject") => {
    if (!selectedInvoice) return;
    const needsComment = action !== "approve";
    const comment = needsComment ? window.prompt(`Reason to ${action} this invoice:`) : undefined;
    if (needsComment && !comment) return;
    try {
      const updated = await api<Invoice>(`/api/supplier-invoices/${selectedInvoice.supplierInvoiceId}/review`, {
        method: "POST", body: { action, comment },
      });
      setSelectedInvoice(updated);
      notify.success(`Invoice ${updated.status.toLowerCase()}.`);
      await loadInvoices();
    } catch (e) { reportInvoiceError(e); }
  };

  const submitExisting = async () => {
    if (!selectedInvoice) return;
    try {
      const submitted = await api<Invoice>(`/api/supplier-invoices/${selectedInvoice.supplierInvoiceId}/submit`, { method: "POST" });
      setSelectedInvoice(submitted);
      notify.success(`Invoice ${submitted.invoiceNumber} was submitted for review.`);
      await Promise.all([loadInvoices(), loadSessions()]);
    } catch (e) { reportInvoiceError(e); }
  };

  const openInvoice = async (invoice: Invoice) => {
    try {
      setSelectedInvoice(await api<Invoice>(`/api/supplier-invoices/${invoice.supplierInvoiceId}`));
    } catch (e) {
      reportInvoiceError(e);
    }
  };

  const editQueriedInvoice = (invoice: Invoice) => {
    setEligibility(null);
    setInvoiceDates({ invoiceDate: invoice.invoiceDate?.slice(0, 10) ?? currentInvoiceDate(), dueDate: invoice.dueDate?.slice(0, 10) ?? "" });
    const editableSessions: EligibleSession[] = invoice.lines.map((line) => ({
      serviceInstanceRotaId: line.serviceInstanceRotaId,
      serviceInstanceId: line.serviceInstanceId,
      serviceId: line.serviceId,
      serviceName: line.serviceName,
      startDateTime: line.sessionStartDateTime,
      endDateTime: line.sessionEndDateTime,
      scheduledHours: Math.round(((new Date(line.sessionEndDateTime).getTime() - new Date(line.sessionStartDateTime).getTime()) / 3600000) * 100) / 100,
      rateType: line.rateType,
      rate: line.rate,
    }));
    setSessions(editableSessions);
    setSelection(Object.fromEntries(invoice.lines.map((line) => [line.serviceInstanceRotaId, {
      selected: true, hours: line.hoursClaimed, reason: line.adjustmentReason ?? "",
      workStart: localWorkDateTime(line.workStartDateTime ?? line.sessionStartDateTime),
      workEnd: localWorkDateTime(line.workEndDateTime ?? new Date(new Date(line.sessionStartDateTime).getTime() + line.hoursClaimed * 3600000).toISOString()),
    }])));
    setEditingInvoiceId(invoice.supplierInvoiceId);
    setSelectedInvoice(null);
    setTab("create");
  };

  return (
    <section className="supplier-invoices-page">
      <PageHeader
        title={tab === "setup" ? selectedSupplierProfileId ? "Supplier details" : "Add supplier" : tab === "create" ? "Create invoice" : "Invoices"}
        subtitle={tab === "setup" ? "Manage supplier and invoice details." : tab === "create" ? "Select completed service sessions to invoice." : "Manage suppliers and review invoices for completed service sessions."}
        actions={headerAction}
      />
      {tab !== "create" && tab !== "setup" && (
        <nav className="supplier-invoice-tabs" aria-label="Invoice sections">
          {isTenantAdmin && <button className={tab === "suppliers" ? "active" : ""} onClick={() => { setTab("suppliers"); setSelectedInvoice(null); setSelectedSupplierProfileId(null); void loadInvoices(); }}>Suppliers</button>}
          {canCreate && <button className={tab === "mine" ? "active" : ""} onClick={() => { setTab("mine"); setSelectedInvoice(null); setSelectedSupplierProfileId(null); }}>My invoices</button>}
          {canReview && <button className={tab === "review" ? "active" : ""} onClick={() => { setTab("review"); setSelectedInvoice(null); setSelectedSupplierProfileId(null); }}>Review</button>}
        </nav>
      )}

      {selectedInvoice ? (
        <InvoiceDetail invoice={selectedInvoice} canReview={canReview} canSubmit={canCreate && selectedInvoice.status === "Draft"} canEdit={canCreate && ["Draft", "QueryRaised"].includes(selectedInvoice.status)} onBack={() => setSelectedInvoice(null)} onReview={reviewInvoice} onSubmit={submitExisting} onEdit={() => editQueriedInvoice(selectedInvoice)} />
      ) : tab === "create" ? (
        <CreateInvoice sessions={sessions} eligibility={eligibility} selection={selection} setSelection={setSelection} invoiceDates={invoiceDates} setInvoiceDates={setInvoiceDates} total={draftTotal} onSave={saveInvoice} />
      ) : tab === "mine" ? (
        <InvoiceList title="My invoices" invoices={mine} empty="You have not created any invoices." onOpen={(invoice) => void openInvoice(invoice)} />
      ) : tab === "suppliers" ? (
        <SupplierDirectory
          suppliers={supplierSummaries}
          onOpen={(supplier) => {
            setSelectedSupplierProfileId(supplier.supplierProfileId);
            setTab("setup");
          }}
        />
      ) : tab === "review" ? (
        <InvoiceList title="Invoices to review" invoices={review} empty="There are no submitted invoices." onOpen={(invoice) => void openInvoice(invoice)} />
      ) : (
        <InvoiceSetup
          tenantId={selectedTenant?.tenantId}
          initialSupplierProfileId={selectedSupplierProfileId}
          onSaved={() => void loadInvoices()}
        />
      )}
    </section>
  );
}

export function CreateInvoice({ sessions, eligibility, selection, setSelection, invoiceDates, setInvoiceDates, total, onSave }: {
  invoiceDates: { invoiceDate: string; dueDate: string };
  setInvoiceDates: React.Dispatch<React.SetStateAction<{ invoiceDate: string; dueDate: string }>>;
  eligibility: SessionEligibility | null;
  sessions: EligibleSession[];
  selection: Record<number, Selection>;
  setSelection: React.Dispatch<React.SetStateAction<Record<number, Selection>>>;
  total: number;
  onSave: (submit: boolean) => Promise<void>;
}) {
  const [selectedServiceId, setSelectedServiceId] = useState(() =>
    String(sessions.find((session) => selection[session.serviceInstanceRotaId]?.selected)?.serviceId ?? ""),
  );
  const [sharedStartTime, setSharedStartTime] = useState("");
  const [sharedFinishTime, setSharedFinishTime] = useState("");
  const [sharedReason, setSharedReason] = useState("");
  const [sharedTimesMessage, setSharedTimesMessage] = useState("");
  const [sharedTimesError, setSharedTimesError] = useState("");
  const services = Array.from(new Map(sessions.map((session) => [session.serviceId, session.serviceName])).entries())
    .sort((a, b) => a[1].localeCompare(b[1]));
  const visibleSessions = sessions.filter((session) => String(session.serviceId) === selectedServiceId);
  const selectedCount = Object.values(selection).filter((x) => x.selected).length;
  const selectedServices = services.filter(([serviceId]) => sessions.some((session) =>
    session.serviceId === serviceId && selection[session.serviceInstanceRotaId]?.selected,
  ));
  const invalidWorkingTime = sessions.some((session) => {
    const value = selection[session.serviceInstanceRotaId];
    return value?.selected && (value.hours <= 0 || value.hours > 24 || !value.workStart || !value.workEnd);
  });
  const invalidDates = !invoiceDates.invoiceDate || (!!invoiceDates.dueDate && invoiceDates.dueDate < invoiceDates.invoiceDate);
  const update = (id: number, values: Partial<Selection>) => setSelection((current) => ({
    ...current, [id]: { ...current[id], ...values },
  }));
  const applySharedTimes = () => {
    setSharedTimesMessage("");
    setSharedTimesError("");
    if (!sharedStartTime || !sharedFinishTime || sharedStartTime === sharedFinishTime) {
      setSharedTimesError("Enter different start and finish times.");
      return;
    }
    const [startHour, startMinute] = sharedStartTime.split(":").map(Number);
    const [endHour, endMinute] = sharedFinishTime.split(":").map(Number);
    const changes: Record<number, Partial<Selection>> = {};
    for (const session of sessions) {
      if (!selection[session.serviceInstanceRotaId]?.selected) continue;
      const start = new Date(session.startDateTime);
      start.setHours(startHour, startMinute, 0, 0);
      const end = new Date(start);
      end.setHours(endHour, endMinute, 0, 0);
      if (end <= start) end.setDate(end.getDate() + 1);
      const workStart = localWorkDateTime(start.toISOString());
      const workEnd = localWorkDateTime(end.toISOString());
      const hours = workingHours(workStart, workEnd);
      if (hours <= 0 || hours > 24 || end <= new Date(session.startDateTime) || start >= new Date(session.endDateTime)) {
        setSharedTimesError(`These times must overlap ${session.serviceName} on ${date(session.startDateTime)} and be within 24 hours. No times were changed.`);
        return;
      }
      changes[session.serviceInstanceRotaId] = {
        workStart, workEnd, hours,
        ...(sharedReason.trim() ? { reason: sharedReason.trim() } : {}),
      };
    }
    setSelection((current) => Object.fromEntries(Object.entries(current).map(([id, value]) =>
      [id, { ...value, ...changes[Number(id)] }],
    )));
    setSharedTimesMessage(`Working times updated for ${Object.keys(changes).length} selected sessions.`);
  };
  return <div className="invoice-workspace-card">
    <div className="invoice-section-heading"><div><h2>Select completed sessions</h2><p>Confirmed sessions from the last 12 months with a rate covering the session date are shown.</p></div><strong>{selectedCount} selected</strong></div>
    <div className="invoice-date-fields">
      <label>Invoice date<input type="date" required value={invoiceDates.invoiceDate} onChange={(event) => setInvoiceDates((current) => ({ ...current, invoiceDate: event.target.value }))} /></label>
      <label>Due date<input type="date" min={invoiceDates.invoiceDate} value={invoiceDates.dueDate} onChange={(event) => setInvoiceDates((current) => ({ ...current, dueDate: event.target.value }))} /><small>Leave blank to use the organisation's payment terms.</small></label>
      {invalidDates && <p role="alert">Enter an invoice date and a due date on or after it.</p>}
    </div>
    {eligibility && <div className="invoice-eligibility-summary">
      <p>Checking assignments for <strong>{eligibility.supplierName}</strong> (person #{eligibility.partyId}), {date(eligibility.fromDate)} to {date(eligibility.toDate)}.</p>
      {eligibility.assignmentCount === 0 && <p>No rota assignments were found for this supplier's linked person in this organisation. Check that the person assigned to the rota is the person linked to your supplier login.</p>}
      {eligibility.excludedSessions.length > 0 && <details>
        <summary>Why sessions are unavailable ({eligibility.excludedSessions.length})</summary>
        <ul>{eligibility.excludedSessions.map((session) => <li key={session.serviceInstanceRotaId}>
          <strong>{session.serviceName} — {date(session.startDateTime)}</strong>
          <p>{session.reasons.join(" ")}</p>
        </li>)}</ul>
      </details>}
    </div>}
    {sessions.length > 0 && <label className="invoice-service-picker">
      Service
      <select value={selectedServiceId} onChange={(event) => setSelectedServiceId(event.target.value)}>
        <option value="">Select a service</option>
        {services.map(([serviceId, name]) => <option key={serviceId} value={serviceId}>{name}</option>)}
      </select>
    </label>}
    {selectedCount > 0 && <div className="invoice-shared-times">
      <strong>Change times for all {selectedCount} selected sessions</strong>
      <p>Applies across selected services, keeping each session's date. An earlier finish time means the following day.</p>
      <div className="invoice-shared-times-fields">
        <label>Start work time<input type="time" value={sharedStartTime} onChange={(event) => setSharedStartTime(event.target.value)} /></label>
        <label>Finish work time<input type="time" value={sharedFinishTime} onChange={(event) => setSharedFinishTime(event.target.value)} /></label>
        <label>Shared adjustment reason (optional)<input value={sharedReason} onChange={(event) => setSharedReason(event.target.value)} placeholder="Preparation and cleanup" /></label>
        <button type="button" className="secondary-button" onClick={applySharedTimes} disabled={!sharedStartTime || !sharedFinishTime || sharedStartTime === sharedFinishTime}>Apply to all selected</button>
      </div>
      {sharedTimesError && <p className="invoice-shared-times-error" role="alert">{sharedTimesError}</p>}
      {sharedTimesMessage && <p role="status">{sharedTimesMessage}</p>}
    </div>}
    {sessions.length === 0 ? <div className="invoice-empty">No sessions are currently available to invoice.</div> :
      !selectedServiceId ? <p className="invoice-session-guidance">Select a service to view its completed sessions.</p> :
      <div className="eligible-session-list">{visibleSessions.map((session) => {
        const value = selection[session.serviceInstanceRotaId];
        const scheduled = session.scheduledHours;
        const workStart = value?.workStart ?? localWorkDateTime(session.startDateTime);
        const workEnd = value?.workEnd ?? localWorkDateTime(session.endDateTime);
        const adjusted = workStart !== localWorkDateTime(session.startDateTime) || workEnd !== localWorkDateTime(session.endDateTime);
        const changeWorkingTime = (start: string, end: string) => update(session.serviceInstanceRotaId, {
          workStart: start, workEnd: end, hours: workingHours(start, end),
        });
        return <article className={`eligible-session ${value?.selected ? "selected" : ""}`} key={session.serviceInstanceRotaId}>
          <label className="eligible-session-main" title={session.serviceName}>
            <input type="checkbox" aria-label={`Select ${session.serviceName} on ${date(session.startDateTime)} at ${time(session.startDateTime)}`} checked={value?.selected ?? false} onChange={(e) => update(session.serviceInstanceRotaId, { selected: e.target.checked, workStart, workEnd, hours: workingHours(workStart, workEnd) })} />
            <strong>{date(session.startDateTime)}</strong>
            <span className="eligible-session-time">{time(session.startDateTime)}–{time(session.endDateTime)}</span>
            <span className="eligible-session-rate">{money.format(session.rate)} / {session.rateType === "Hourly" ? "hour" : "session"}</span>
          </label>
          <div className="eligible-session-hours">
            <span>{value?.selected ? value.hours : scheduled} hrs</span>
          </div>
          {value?.selected && <div className="eligible-session-working-times">
            <label>Start work<input type="datetime-local" aria-label={`Start work for ${session.serviceName} on ${date(session.startDateTime)}`} value={workStart} onChange={(e) => changeWorkingTime(e.target.value, workEnd)} /></label>
            <label>Finish work<input type="datetime-local" aria-label={`Finish work for ${session.serviceName} on ${date(session.startDateTime)}`} value={workEnd} onChange={(e) => changeWorkingTime(workStart, e.target.value)} /></label>
            {(value.hours <= 0 || value.hours > 24 || !workStart || !workEnd) && <p role="alert">Enter a finish time after the start, within 24 hours.</p>}
          </div>}
          {value?.selected && adjusted && <label className="eligible-session-reason">Reason for adjusted working times<input required value={value.reason} onChange={(e) => update(session.serviceInstanceRotaId, { reason: e.target.value })} /></label>}
        </article>;
      })}</div>}
    {selectedServices.length > 0 && <p className="invoice-session-guidance">Selected services: {selectedServices.map(([, name]) => name).join(", ")}. Selections are kept when changing service.</p>}
    <footer className="invoice-draft-footer"><div><span>Estimated subtotal</span><strong>{money.format(total)}</strong></div><div className="invoice-actions"><button className="secondary-button" disabled={!selectedCount || invalidWorkingTime || invalidDates} onClick={() => void onSave(false)}>Save draft</button><button className="primary-button" disabled={!selectedCount || invalidWorkingTime || invalidDates} onClick={() => void onSave(true)}>Submit for review</button></div></footer>
  </div>;
}

function SupplierDirectory({ suppliers, onOpen }: { suppliers: SupplierSummary[]; onOpen: (supplier: SupplierSummary) => void }) {
  return <div className="supplier-directory">
    {suppliers.length === 0 ? <div className="card invoice-empty">No suppliers have been configured.</div> : <div className="supplier-card-grid">{suppliers.map((supplier) =>
      <button className="supplier-summary-card" key={`${supplier.supplierProfileId}-${supplier.invoiceEmailAddress}`} onClick={() => onOpen(supplier)}>
        <span className="supplier-summary-main">
          <span className="supplier-summary-icon" aria-hidden="true"><BusinessCenterOutlinedIcon /></span>
          <span className="supplier-summary-copy">
            <strong>{supplier.supplierName}</strong>
            <span>{supplier.invoiceEmailAddress}</span>
            <span className="supplier-summary-meta">
              <span className={`supplier-status-dot ${supplier.isActive === false ? "is-inactive" : ""}`} />
              {supplier.isActive === false ? "Inactive" : "Active"}
              <span aria-hidden="true">·</span>
              {supplier.invoiceCount} {supplier.invoiceCount === 1 ? "invoice" : "invoices"}
              {supplier.invoiceCount > 0 && <><span aria-hidden="true">·</span>{money.format(supplier.invoiceTotal)}</>}
            </span>
          </span>
        </span>
        <span className="supplier-summary-rail" aria-hidden="true"><ChevronRightIcon fontSize="small" /></span>
      </button>)}</div>}
  </div>;
}

function InvoiceList({ title, invoices, empty, onOpen, onBack }: { title: string; invoices: Invoice[]; empty: string; onOpen: (invoice: Invoice) => void; onBack?: () => void }) {
  return <div className="invoice-workspace-card"><div className="invoice-section-heading"><div className="invoice-list-heading">{onBack && <button type="button" className="text-button" onClick={onBack}>← Suppliers</button>}<h2>{title}</h2></div><span>{invoices.length}</span></div>
    {invoices.length === 0 ? <div className="invoice-empty">{empty}</div> : <div className="invoice-list">{invoices.map((invoice) =>
      <button className="invoice-list-row" key={invoice.supplierInvoiceId} onClick={() => onOpen(invoice)}>
        <span><strong>{invoice.invoiceNumber ?? "Draft invoice"}</strong><small>{invoice.supplierName} · {date(invoice.invoiceDate)}</small></span>
        <span className={`invoice-status status-${invoice.status.toLowerCase()}`}>{invoice.status.replace(/([A-Z])/g, " $1").trim()}</span>
        <strong>{money.format(invoice.total)}</strong><span aria-hidden>›</span>
      </button>)}</div>}
  </div>;
}

export function InvoiceDetail({ invoice, canReview, canSubmit, canEdit, onBack, onReview, onSubmit, onEdit }: { invoice: Invoice; canReview: boolean; canSubmit: boolean; canEdit: boolean; onBack: () => void; onReview: (action: "approve" | "query" | "reject") => Promise<void>; onSubmit: () => Promise<void>; onEdit: () => void }) {
  const reference = invoice.invoiceNumber ?? `Draft-${invoice.supplierInvoiceId}`;
  const generateDocument = useCallback(() => api<Blob>(`/api/supplier-invoices/${invoice.supplierInvoiceId}/pdf`, { responseType: "blob", notifyError: false }), [invoice]);
  const serviceIds = [...new Set(invoice.lines.map((line) => line.serviceId))];
  return <article className="invoice-workspace-card invoice-detail">
    <div className="invoice-detail-toolbar"><button className="text-button" onClick={onBack}>← Back</button><DocumentActions documentType="supplier-invoice" title={`Invoice ${reference}`} fileName={`Invoice-${reference}.pdf`} generateDocument={generateDocument} shareTitle={`Invoice ${reference}`} shareText={`Invoice from ${invoice.supplierName}`} compact /></div>
    <div className="invoice-document-header"><div><small>Invoice</small><h2>{invoice.invoiceNumber ?? "Draft"}</h2><span className={`invoice-status status-${invoice.status.toLowerCase()}`}>{invoice.status}</span></div><div><strong>{invoice.supplierName}</strong><span>{invoice.supplierEmailAddress}</span>{invoice.supplierIsSelfEmployed && <span className="invoice-supplier-classification">Self-employed supplier</span>}</div></div>
    <div className="invoice-meta"><div><span>Invoice date</span><strong>{date(invoice.invoiceDate)}</strong></div><div><span>Due date</span><strong>{date(invoice.dueDate)}</strong></div><div><span>Invoice to</span><strong>{invoice.tenantName}</strong></div></div>
    {serviceIds.map((serviceId) => {
      const lines = invoice.lines.filter((line) => line.serviceId === serviceId);
      return <section className="invoice-service-section" key={serviceId}>
        <h3>{lines[0].serviceName}</h3>
        <div className="invoice-lines"><div className="invoice-line invoice-line--header"><span>Date / times / details</span><span>Hours</span><span>Rate</span><span>Total</span></div>{lines.map((line) => {
          const start = line.workStartDateTime ?? line.sessionStartDateTime;
          const end = line.workEndDateTime ?? line.sessionEndDateTime;
          return <div className="invoice-line" key={line.supplierInvoiceLineId}><span><small>{date(start)} · {time(start)} – {date(start) !== date(end) && `${date(end)} `}{time(end)}</small>{line.adjustmentReason && <small>{line.adjustmentReason}</small>}</span><span>{line.hoursClaimed}</span><span>{money.format(line.rate)}</span><strong>{money.format(line.lineTotal)}</strong></div>;
        })}</div>
      </section>;
    })}
    <div className="invoice-totals"><div><span>Subtotal</span><strong>{money.format(invoice.subtotal)}</strong></div>{invoice.vatAmount > 0 && <div><span>VAT</span><strong>{money.format(invoice.vatAmount)}</strong></div>}<div className="invoice-total"><span>Total</span><strong>{money.format(invoice.total)}</strong></div></div>
    {invoice.paymentDetails && <section className="invoice-payment-details"><h3>Payment details</h3>{invoice.paymentDetails.bankName && <div><span>Bank</span><strong>{invoice.paymentDetails.bankName}</strong></div>}<div><span>Account name</span><strong>{invoice.paymentDetails.bankAccountName}</strong></div><div><span>Sort code</span><strong>{invoice.paymentDetails.sortCode}</strong></div><div><span>Account number</span><strong>{invoice.paymentDetails.accountNumber}</strong></div>{invoice.paymentDetails.paymentReference && <div><span>Payment reference</span><strong>{invoice.paymentDetails.paymentReference}</strong></div>}</section>}
    <p className="invoice-worker-statement">This invoice is issued by a self-employed sessional worker for services provided. The worker is responsible for managing their own tax affairs, including reporting their income and paying any tax due.</p>
    {invoice.reviewComment && <div className="invoice-review-comment"><strong>Review comment</strong><p>{invoice.reviewComment}</p></div>}
    {canSubmit && <div className="invoice-review-actions"><button className="primary-button" onClick={() => void onSubmit()}>Submit for review</button></div>}
    {canEdit && <div className="invoice-review-actions"><button className="primary-button" onClick={onEdit}>{invoice.status === "Draft" ? "Edit draft" : "Update queried invoice"}</button></div>}
    {canReview && invoice.status === "InReview" && <div className="invoice-review-actions"><button className="secondary-button" onClick={() => void onReview("query")}>Raise query</button><button className="danger-button" onClick={() => void onReview("reject")}>Reject</button><button className="primary-button" onClick={() => void onReview("approve")}>Approve</button></div>}
  </article>;
}

function InvoiceSetup({ tenantId, initialSupplierProfileId, onSaved }: { tenantId?: number; initialSupplierProfileId: number | null; onSaved: () => void }) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [rates, setRates] = useState<SupplierRate[]>([]);
  const [editingRate, setEditingRate] = useState<SupplierRate | null>(null);
  const [supplier, setSupplier] = useState({ supplierProfileId: 0, userId: "", isSelfEmployed: false, vatRegistered: false, vatRegistrationNumber: "", vatRate: 0, invoicePrefix: "INV", bankAccountName: "", bankName: "", sortCode: "", accountNumber: "", paymentReference: "", bankAccountLastFour: "" });
  const [rate, setRate] = useState({ supplierProfileId: 0, serviceId: 0, rateType: "Hourly", rate: 0, effectiveFrom: new Date().toISOString().slice(0, 10), effectiveTo: "" });
  const availableServices = useMemo(
    () => services.filter((service) => !rates.some((existingRate) => existingRate.serviceId === service.serviceId)),
    [services, rates],
  );

  const load = useCallback(async () => {
    const [supplierRows, userRows, serviceRows] = await Promise.all([
      api<Supplier[]>("/api/supplier-invoices/suppliers"),
      api<UserOption[]>("/api/platformuser/get-all-users-include-roles"),
      tenantId ? api<ServiceOption[]>(`/api/platformuser/tenant-services/${tenantId}`) : Promise.resolve([]),
    ]);
    setSuppliers(supplierRows); setUsers(userRows); setServices(serviceRows);
  }, [tenantId]);
  useEffect(() => { void load().catch((e) => reportInvoiceError(e)); }, [load]);

  const loadRates = useCallback(async (supplierProfileId: number) => {
    if (!supplierProfileId) { setRates([]); return; }
    setRates(await api<SupplierRate[]>(`/api/supplier-invoices/suppliers/${supplierProfileId}/rates`));
    setEditingRate(null);
  }, []);

  useEffect(() => {
    if (initialSupplierProfileId && suppliers.some((item) => item.supplierProfileId === initialSupplierProfileId)) {
      selectSupplier(initialSupplierProfileId);
    }
  // selectSupplier only maps the supplier collection into the form.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSupplierProfileId, suppliers]);

  const selectSupplier = (supplierProfileId: number) => {
    const existing = suppliers.find((item) => item.supplierProfileId === supplierProfileId);
    if (!existing) {
      setSupplier({ supplierProfileId: 0, userId: "", isSelfEmployed: false, vatRegistered: false, vatRegistrationNumber: "", vatRate: 0, invoicePrefix: "INV", bankAccountName: "", bankName: "", sortCode: "", accountNumber: "", paymentReference: "", bankAccountLastFour: "" });
      setRate((current) => ({ ...current, supplierProfileId: 0 }));
      setRates([]);
      setEditingRate(null);
      return;
    }
    setSupplier({
      supplierProfileId: existing.supplierProfileId,
      userId: existing.userId ?? "",
      isSelfEmployed: existing.isSelfEmployed,
      vatRegistered: existing.vatRegistered ?? false,
      vatRegistrationNumber: existing.vatRegistrationNumber ?? "",
      vatRate: existing.vatRate ?? 0,
      invoicePrefix: existing.invoicePrefix ?? "INV",
      bankAccountName: "",
      bankName: "",
      sortCode: "",
      accountNumber: "",
      paymentReference: "",
      bankAccountLastFour: existing.bankAccountLastFour ?? "",
    });
    setRate((current) => ({ ...current, supplierProfileId: existing.supplierProfileId }));
    void loadRates(existing.supplierProfileId).catch((e) => reportInvoiceError(e));
  };

  const saveSupplier = async () => {
    try { const saved = await api<Supplier>("/api/supplier-invoices/suppliers", { method: "POST", body: supplier }); setSuppliers((x) => [...x.filter((s) => s.supplierProfileId !== saved.supplierProfileId), saved]); setSupplier((current) => ({ ...current, supplierProfileId: saved.supplierProfileId, isSelfEmployed: saved.isSelfEmployed, bankAccountName: "", bankName: "", sortCode: "", accountNumber: "", paymentReference: "", bankAccountLastFour: saved.bankAccountLastFour ?? "" })); setRate((current) => ({ ...current, supplierProfileId: saved.supplierProfileId })); notify.success("Supplier profile saved."); onSaved(); }
    catch (e) { reportInvoiceError(e); }
  };
  const saveRate = async () => {
    try {
      const saved = await api<SupplierRate>(`/api/supplier-invoices/suppliers/${rate.supplierProfileId}/rates`, { method: "POST", body: { ...rate, effectiveFrom: rate.effectiveFrom, effectiveTo: rate.effectiveTo || null } });
      setRates((current) => [...current, saved].sort((a, b) => a.serviceName.localeCompare(b.serviceName) || b.effectiveFrom.localeCompare(a.effectiveFrom)));
      setRate((current) => ({ ...current, serviceId: 0, rate: 0, effectiveTo: "" }));
      notify.success("Supplier rate added.");
    }
    catch (e) { reportInvoiceError(e); }
  };
  const startEditingRate = (item: SupplierRate) => {
    setEditingRate({
      ...item,
      effectiveFrom: item.effectiveFrom.slice(0, 10),
      effectiveTo: item.effectiveTo?.slice(0, 10) ?? "",
    });
    };
  const saveEditedRate = async () => {
    if (!editingRate) return;
    try {
      const saved = await api<SupplierRate>(`/api/supplier-invoices/suppliers/${editingRate.supplierProfileId}/rates/${editingRate.supplierServiceRateId}`, {
        method: "PUT",
        body: {
          serviceId: editingRate.serviceId,
          rateType: editingRate.rateType,
          rate: editingRate.rate,
          effectiveFrom: editingRate.effectiveFrom,
          effectiveTo: editingRate.effectiveTo || null,
        },
      });
      setRates((current) => current.map((item) => item.supplierServiceRateId === saved.supplierServiceRateId ? saved : item));
      setEditingRate(null);
      notify.success("Supplier rate updated.");
    }
    catch (e) { reportInvoiceError(e); }
  };

  return <div className="invoice-setup-layout">

    <section className="invoice-workspace-card supplier-details-card">
      <div className="supplier-form-heading"><div><h2>Supplier details</h2><p>Select an organisation user with the Self-Employed Staff role.</p></div></div>
      <div className="supplier-profile-fields">
        <label className="supplier-field-wide">Supplier
          <select value={supplier.userId} disabled={supplier.supplierProfileId > 0} onChange={(e) => setSupplier({ ...supplier, userId: e.target.value })}>
            <option value="">Select supplier</option>
            {users.filter((u) => u.tenants?.some((t) => t.roles.includes(TENANT_ROLE_CODES.SELF_EMPLOYED_STAFF))).map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName} · {u.email}</option>)}
          </select>
        </label>
        <label>Invoice prefix<input value={supplier.invoicePrefix} onChange={(e) => setSupplier({ ...supplier, invoicePrefix: e.target.value })} /></label>
        <label className="invoice-checkbox supplier-checkbox"><input type="checkbox" checked={supplier.isSelfEmployed} onChange={(e) => setSupplier({ ...supplier, isSelfEmployed: e.target.checked })} />Self-employed supplier</label>
        <label className="invoice-checkbox supplier-checkbox"><input type="checkbox" checked={supplier.vatRegistered} onChange={(e) => setSupplier({ ...supplier, vatRegistered: e.target.checked, vatRate: e.target.checked ? supplier.vatRate : 0 })} />VAT registered</label>
        {supplier.vatRegistered && <>
          <label>VAT registration number<input value={supplier.vatRegistrationNumber} onChange={(e) => setSupplier({ ...supplier, vatRegistrationNumber: e.target.value })} /></label>
          <label>VAT rate (%)<input type="number" min="0" max="100" step="0.01" value={supplier.vatRate} onChange={(e) => setSupplier({ ...supplier, vatRate: Number(e.target.value) })} /></label>
        </>}
      </div>

      {supplier.userId && <UserPartyLink key={supplier.userId} userId={supplier.userId} />}
      <div className="invoice-payment-form-heading"><strong>Bank and payment details</strong>{supplier.bankAccountLastFour && <span>Saved account ending {supplier.bankAccountLastFour}</span>}</div>
      <div className="supplier-payment-fields">
        <label>Account name<input autoComplete="off" value={supplier.bankAccountName} onChange={(e) => setSupplier({ ...supplier, bankAccountName: e.target.value })} placeholder={supplier.bankAccountLastFour ? "Leave blank to keep saved details" : ""} /></label>
        <label>Bank name (optional)<input autoComplete="off" value={supplier.bankName} onChange={(e) => setSupplier({ ...supplier, bankName: e.target.value })} /></label>
        <label>Sort code<input autoComplete="off" inputMode="numeric" value={supplier.sortCode} onChange={(e) => setSupplier({ ...supplier, sortCode: e.target.value })} placeholder="00-00-00" /></label>
        <label>Account number<input autoComplete="off" inputMode="numeric" value={supplier.accountNumber} onChange={(e) => setSupplier({ ...supplier, accountNumber: e.target.value })} /></label>
        <label>Payment reference (optional)<input autoComplete="off" value={supplier.paymentReference} onChange={(e) => setSupplier({ ...supplier, paymentReference: e.target.value })} /></label>
      </div>
      <div className="supplier-form-actions"><button className="primary-button" disabled={!supplier.userId} onClick={() => void saveSupplier()}>{supplier.supplierProfileId ? "Save supplier" : "Add supplier"}</button></div>
    </section>

    {supplier.supplierProfileId > 0 && <section className="invoice-workspace-card supplier-rates-card">
      <div className="supplier-form-heading"><div><h2>Service rates</h2><p>Add separate rates for each service and effective date range.</p></div></div>
      <div className="supplier-rate-table-wrap">
        <table className="supplier-rate-table">
          <thead><tr><th>Service</th><th>Rate type</th><th>Rate</th><th>Effective from</th><th>Effective to</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            <tr className="supplier-rate-entry">
              <td><select aria-label="Service" value={rate.serviceId} onChange={(e) => setRate({ ...rate, serviceId: Number(e.target.value) })}><option value={0}>{availableServices.length ? "Select service" : "All services configured"}</option>{availableServices.map((s) => <option key={s.serviceId} value={s.serviceId}>{s.name}</option>)}</select></td>
              <td><select aria-label="Rate type" value={rate.rateType} onChange={(e) => setRate({ ...rate, rateType: e.target.value })}><option value="Hourly">Hourly</option><option value="PerSession">Per session</option></select></td>
              <td><input aria-label="Rate" type="number" min="0" step="0.01" value={rate.rate} onChange={(e) => setRate({ ...rate, rate: Number(e.target.value) })} /></td>
              <td><input aria-label="Effective from" type="date" value={rate.effectiveFrom} onChange={(e) => setRate({ ...rate, effectiveFrom: e.target.value })} /></td>
              <td><input aria-label="Effective to" type="date" value={rate.effectiveTo} onChange={(e) => setRate({ ...rate, effectiveTo: e.target.value })} /></td>
              <td><span className="supplier-rate-status is-active">New</span></td>
              <td><button className="primary-button" disabled={!rate.serviceId} onClick={() => void saveRate()}>Add rate</button></td>
            </tr>
            {rates.length === 0 && <tr><td className="supplier-rate-empty" colSpan={7}>No service rates have been added.</td></tr>}
            {rates.map((item) => {
              const editableRate = editingRate?.supplierServiceRateId === item.supplierServiceRateId ? editingRate : null;
              const isEditing = editableRate !== null;
              return <tr className={isEditing ? "supplier-rate-editing" : ""} key={item.supplierServiceRateId}>
                <td><strong>{item.serviceName}</strong></td>
                <td>{isEditing
                  ? <select aria-label={`Rate type for ${item.serviceName}`} value={editableRate.rateType} onChange={(e) => setEditingRate({ ...editableRate, rateType: e.target.value })}><option value="Hourly">Hourly</option><option value="PerSession">Per session</option></select>
                  : item.rateType === "PerSession" ? "Per session" : item.rateType}</td>
                <td>{isEditing
                  ? <input aria-label={`Rate for ${item.serviceName}`} type="number" min="0" step="0.01" value={editableRate.rate} onChange={(e) => setEditingRate({ ...editableRate, rate: Number(e.target.value) })} />
                  : money.format(item.rate)}</td>
                <td>{isEditing
                  ? <input aria-label={`Effective from for ${item.serviceName}`} type="date" value={editableRate.effectiveFrom} onChange={(e) => setEditingRate({ ...editableRate, effectiveFrom: e.target.value })} />
                  : date(item.effectiveFrom)}</td>
                <td>{isEditing
                  ? <input aria-label={`Effective to for ${item.serviceName}`} type="date" value={editableRate.effectiveTo ?? ""} onChange={(e) => setEditingRate({ ...editableRate, effectiveTo: e.target.value })} />
                  : item.effectiveTo ? date(item.effectiveTo) : "Ongoing"}</td>
                <td><span className={`supplier-rate-status ${item.isActive ? "is-active" : "is-inactive"}`}>{item.isActive ? "Active" : "Inactive"}</span></td>
                <td>{isEditing
                  ? <div className="supplier-rate-actions"><button className="primary-button" onClick={() => void saveEditedRate()}>Save</button><button className="secondary-button" onClick={() => setEditingRate(null)}>Cancel</button></div>
                  : <button className="secondary-button" onClick={() => startEditingRate(item)}>Edit</button>}</td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
    </section>}
  </div>;
}
